import { useTranslation } from "@redotech/redo-i18n";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import {
  AddressCardContent,
  AddressChangedFields,
  AddressFields,
  AddressSelection,
  AddressSelectionRadioGroup,
  SuggestedAddressContent,
} from "@redotech/redo-web/address-verification/address-suggestion";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { PortalSelectionCard } from "./portal-selection-card";

export interface AddressDisplayProps {
  title: string;
  showBorder?: boolean;
  continueDisabled?: boolean;
  onEdit?: () => void;
  blockingErrorMessage?: string;
  addressFields?: AddressFields;
  showComparison: boolean;
  comparisonDirection: "row" | "column";
  comparisonDisabled: boolean;
  comparisonHasError: boolean;
  comparisonIsLoading: boolean;
  changedFields?: AddressChangedFields;
  selected: AddressSelection;
  onSelect: (selection: AddressSelection) => void;
  suggestedAddressFields?: Omit<AddressFields, "name">;
  missingEmailErrorMessage?: string;
  modalless?: boolean;
}

export function AddressDisplay({
  title,
  showBorder = true,
  continueDisabled = false,
  onEdit,
  blockingErrorMessage,
  addressFields,
  showComparison,
  comparisonDirection,
  comparisonDisabled,
  comparisonHasError,
  comparisonIsLoading,
  changedFields,
  selected,
  onSelect,
  suggestedAddressFields,
  missingEmailErrorMessage,
  modalless = false,
}: AddressDisplayProps) {
  const { t } = useTranslation();

  const comparison =
    showComparison && addressFields ? (
      modalless ? (
        // Always stacked: the modalless address screen renders in a narrow
        // content column, which is too tight for two cards side by side.
        <Flex dir="column" gap="lg">
          <PortalSelectionCard
            disabled={comparisonDisabled || comparisonHasError}
            inputType="radio"
            onClick={() => onSelect("suggested")}
            selected={!comparisonHasError && selected === "suggested"}
            title={
              <SuggestedAddressContent
                address={suggestedAddressFields}
                changedFields={comparisonHasError ? undefined : changedFields}
                hasError={comparisonHasError}
                isLoading={comparisonIsLoading}
              />
            }
            uiSelector="option"
          />
          <PortalSelectionCard
            disabled={comparisonDisabled || comparisonHasError}
            inputType="radio"
            onClick={() => onSelect("original")}
            selected={!comparisonHasError && selected === "original"}
            title={
              <AddressCardContent
                changedFields={comparisonHasError ? undefined : changedFields}
                fields={addressFields}
                title={t("Original address")}
                variant="original"
              />
            }
            uiSelector="option"
          />
        </Flex>
      ) : (
        <AddressSelectionRadioGroup
          changedFields={changedFields}
          direction={comparisonDirection}
          disabled={comparisonDisabled}
          fullWidth
          hasError={comparisonHasError}
          isLoading={comparisonIsLoading}
          onSelect={onSelect}
          originalFields={addressFields}
          originalTitle={t("Original address")}
          selected={selected}
          suggestedAddress={suggestedAddressFields}
          uiSelector="option"
        />
      )
    ) : null;

  const content = (
    <Flex dir="column" gap="md" w="full">
      <Flex align="flex-start" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {title}
        </Text>
        {onEdit && (
          <Flex align="center" justify="center">
            <RedoButton
              dataTestId="address-edit-button"
              hierarchy={continueDisabled ? "primary" : "secondary"}
              onClick={onEdit}
              size="md"
              text={t("Edit")}
              uiSelector="edit-button"
            />
          </Flex>
        )}
      </Flex>

      {blockingErrorMessage && (
        <AddressError errorMessage={blockingErrorMessage} />
      )}

      {!blockingErrorMessage && !addressFields && (
        <Text fontSize="sm" textColor="secondary">
          {t("Same as returning items from.")}
        </Text>
      )}

      {comparison}

      {!blockingErrorMessage && addressFields && !showComparison && (
        <AddressCardContent fields={addressFields} variant="original" />
      )}

      {missingEmailErrorMessage && (
        <Flex dir="column" gap="md" mt="md">
          <AddressError errorMessage={missingEmailErrorMessage} />
        </Flex>
      )}
    </Flex>
  );

  const effectiveShowBorder = modalless ? false : showBorder;

  return (
    <Flex
      borderColor={effectiveShowBorder ? "secondary" : "transparent"}
      borderRadius="lg"
      borderStyle={effectiveShowBorder ? "solid" : "none"}
      borderWidth="1px"
      data-ui="address"
      px={effectiveShowBorder ? "2xl" : "none"}
      py="xl"
    >
      {content}
    </Flex>
  );
}

export function AddressValidationError({
  errorMessage,
}: {
  errorMessage: string;
}) {
  return <AddressError errorMessage={errorMessage} />;
}

function AddressError({ errorMessage }: { errorMessage: string }) {
  return (
    <Flex
      align="center"
      borderColor="error"
      borderRadius="lg"
      borderStyle="solid"
      borderWidth="1px"
      className="w-full"
      gap="lg"
      p="sm"
    >
      <RedoFeaturedIcon color="error" Icon={AlertCircleIcon} size="sm" />
      <Text fontSize="sm" textColor="error">
        {errorMessage}
      </Text>
    </Flex>
  );
}
